<template>
  <div class="header-experiences-dropdown w-full shadow-lg">
    <div class="header-experiences-dropdown__container">
      <NuxtLink v-for="theme in themesWithUrls" :key="theme.id" class="header-experiences-dropdown__item group" :to="theme.link" @click="onClickTheme(theme.titleEn)">
        <div class="header-experiences-dropdown__image-wrapper">
          <FeaturedThumbnailImage :alt="theme.titleEn" :src="theme.imageUrl" />
          <FeaturedThumbnailTitle>{{ theme.titleEn }}</FeaturedThumbnailTitle>
        </div>
      </NuxtLink>
    </div>
    <div class="header-experiences-dropdown__footer">
      <button class="header-experiences-dropdown__view-more" type="button" @click="onClickViewMore">
        <span>View more</span>
        <span class="header-experiences-dropdown__view-more-arrow" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCurrentTabIndex } from '@/composables/ui/useCurrentTabIndex';
import { useGlobalModal } from '@/composables/ui/useGlobalModal';
import FeaturedThumbnailImage from '@/wabunka-src/common/features/Featured/components/FeaturedThumbnailImage/FeaturedThumbnailImage.vue';
import FeaturedThumbnailTitle from '@/wabunka-src/common/features/Featured/components/FeaturedThumbnailImage/FeaturedThumbnailTitle.vue';
import { getBackgroundImageUrl, type MinimumFeaturedListViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import { sendGAWabunkaClickHeaderNavEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickHeaderNav';

interface Props {
  themes: MinimumFeaturedListViewModel;
}

const props = defineProps<Props>();

const { open } = useGlobalModal('searchExperiencesModal');
const { updateTabIndex } = useCurrentTabIndex('searchExperiencesModalTabIndex');

const themesWithUrls = computed(() =>
  props.themes.map((theme) => ({
    ...theme,
    link: `/japan/${theme.id}/`,
    imageUrl: getBackgroundImageUrl(theme),
  })),
);

const onClickTheme = (titleEn: string) => {
  sendGAWabunkaClickHeaderNavEvent('experiences', titleEn, 'header');
};

const onClickViewMore = () => {
  sendGAWabunkaClickHeaderNavEvent('experiences', 'view_more', 'header');
  updateTabIndex(2); // Themes tab
  open();
};
</script>

<style lang="scss" scoped>
.header-experiences-dropdown {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  &__container {
    display: flex;
    justify-content: center;
    gap: 4px;
    max-width: 1400px;
    margin: 0 auto;
    padding: 32px 48px 8px;
  }
  &__item {
    flex: 1 1 0;
    max-width: 250px;
    min-width: 0;
  }
  &__image-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 91 / 114;
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 48px 24px;
  }
  &__view-more {
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    vertical-align: middle;
    color: #171717;
    transition: color 0.3s ease;
    &:hover {
      color: #b8860b;
      .header-experiences-dropdown__view-more-arrow {
        border-color: #b8860b;
      }
    }
  }
  &__view-more-arrow {
    width: 6px;
    height: 6px;
    border-top: solid 1px #333;
    border-right: solid 1px #333;
    transform: rotate(45deg);
    transition: border-color 0.3s ease;
  }
}
</style>
